import React from "react";
import heroImage from "../assets/heroImage.jpg";
import { FiArrowRight } from "react-icons/fi";
import { HiOutlineChevronDown } from "react-icons/hi";
import { Link } from "react-scroll";
import { motion, useReducedMotion } from "framer-motion";
import { fadeUpItem, staggerContainer } from "../motionPresets";

const Home = () => {
  const reduced = useReducedMotion();
  const stats = [
    { label: "Focus", value: "Full stack · APIs · UI" },
    { label: "Production areas", value: "HRM, LMS, finance & ops" },
    { label: "Core stack", value: "Node · React · Next · GraphQL" },
  ];

  const techPills = [
    "Node.js",
    "React",
    "Next.js",
    "GraphQL",
    "Spring Boot",
    "TypeScript",
    "MySQL",
    "Tailwind",
  ];

  return (
    <div
      name="home"
      className="w-full min-h-[calc(100dvh-1px)] flex flex-col bg-[#030712] relative overflow-hidden"
    >
      <div className="absolute inset-0 site-grid pointer-events-none opacity-90" />
      <div className="absolute top-28 right-[-8%] w-[min(440px,85vw)] h-[min(440px,85vw)] rounded-full bg-sky-500/[0.075] blur-[100px] pointer-events-none" />
      <div className="absolute bottom-[12%] left-[-12%] w-[min(380px,80vw)] h-[min(380px,80vw)] rounded-full bg-indigo-600/[0.065] blur-[90px] pointer-events-none" />

      <div className="flex-1 flex flex-col pt-[5.25rem] md:pt-[5.75rem] pb-8 md:pb-10 px-4 sm:px-6 relative z-10 max-w-7xl mx-auto w-full">
        {/* Main hero row — fills upper area */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10 lg:gap-12 xl:gap-16 flex-1 min-h-0 py-4 md:py-6 lg:py-8">
          <motion.div
            className="flex flex-col justify-center order-2 lg:order-1 text-center lg:text-left flex-1 min-w-0 max-w-2xl lg:max-w-none mx-auto lg:mx-0"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <p className="text-sky-500 font-semibold tracking-[0.18em] uppercase text-[11px] sm:text-xs mb-3">
              Muhammed Nasmi
            </p>
            <h1 className="text-[2rem] sm:text-5xl md:text-[3rem] lg:text-[3.25rem] xl:text-[3.5rem] font-semibold text-white leading-[1.12] tracking-tight">
              Full stack developer building{" "}
              <span className="bg-gradient-to-r from-sky-400 to-indigo-400 bg-clip-text text-transparent">
                reliable products
              </span>
            </h1>
            <p className="text-slate-400 py-5 sm:py-6 text-[15px] sm:text-lg leading-relaxed max-w-xl lg:max-w-2xl mx-auto lg:mx-0">
              Production-focused engineer with experience across HRM, accounting, and learning platforms —
              using{" "}
              <span className="text-slate-300">Node.js, React, Next.js, GraphQL, and Spring Boot</span>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Link
                to="career"
                smooth
                duration={500}
                offset={-10}
                className="group text-white w-full sm:w-fit px-6 py-3.5 flex items-center justify-center rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 cursor-pointer hover:from-sky-500 hover:to-indigo-500 transition-all text-sm font-semibold shadow-lg shadow-sky-900/40 ring-1 ring-white/10"
              >
                View experience
                <span className="group-hover:translate-x-0.5 duration-200">
                  <FiArrowRight size={20} className="ml-2" />
                </span>
              </Link>
              <Link
                to="projects"
                smooth
                duration={500}
                offset={-10}
                className="w-full sm:w-fit px-6 py-3.5 flex items-center justify-center rounded-xl border border-white/[0.12] text-slate-200 hover:bg-white/[0.04] hover:border-sky-500/30 transition-all cursor-pointer text-sm font-semibold"
              >
                Selected projects
              </Link>
            </div>
          </motion.div>

          <motion.div
            className="order-1 lg:order-2 shrink-0 flex flex-col items-center lg:items-end justify-center"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, delay: 0.06 }}
          >
            <motion.div
              className="relative mx-auto lg:mx-0 w-fit"
              animate={
                reduced
                  ? undefined
                  : {
                      y: [0, -7, 0],
                    }
              }
              transition={{
                duration: 5.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-sky-500/35 via-transparent to-indigo-500/28 blur-lg opacity-95" />
              <img
                src={heroImage}
                alt="Muhammed Nasmi"
                className="relative rounded-2xl w-[11.5rem] sm:w-52 md:w-56 lg:w-[15rem] xl:w-[17rem] object-cover aspect-[4/5] ring-1 ring-white/10 shadow-2xl shadow-black/50"
              />
            </motion.div>
            <p className="text-center lg:text-right text-slate-500 text-sm mt-4 font-medium">
              Kandy, Sri Lanka
            </p>
          </motion.div>
        </div>

        {/* Bottom band — fills viewport and reduces “empty” feel */}
        <motion.div
          className="mt-auto pt-6 md:pt-8 border-t border-white/[0.06]"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-7"
            variants={staggerContainer}
            initial={reduced ? false : "hidden"}
            animate="visible"
          >
            {stats.map((s) => (
              <motion.div
                key={s.label}
                variants={fadeUpItem}
                className="rounded-xl border border-white/[0.07] bg-slate-900/40 backdrop-blur-sm px-4 py-3.5 text-center sm:text-left ring-1 ring-white/[0.03]"
              >
                <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-500/90 mb-1">
                  {s.label}
                </p>
                <p className="text-sm text-slate-200 font-medium leading-snug">{s.value}</p>
              </motion.div>
            ))}
          </motion.div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
            <motion.div
              className="flex flex-wrap justify-center md:justify-start gap-2"
              variants={staggerContainer}
              initial={reduced ? false : "hidden"}
              animate="visible"
            >
              {techPills.map((t) => (
                <motion.span
                  key={t}
                  variants={fadeUpItem}
                  whileHover={reduced ? undefined : { y: -2 }}
                  className="text-[11px] sm:text-xs font-medium px-2.5 py-1 rounded-md bg-white/[0.04] text-slate-400 border border-white/[0.06] hover:border-sky-500/25 hover:text-slate-300 transition-colors"
                >
                  {t}
                </motion.span>
              ))}
            </motion.div>

            <Link
              to="career"
              smooth
              duration={500}
              offset={-10}
              className="flex items-center justify-center md:justify-end gap-2 text-slate-500 hover:text-sky-400 text-xs font-medium uppercase tracking-wider cursor-pointer group shrink-0"
            >
              Continue below
              <HiOutlineChevronDown className="text-lg animate-bounce group-hover:text-sky-400" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Home;
